import { useState } from 'react';
import { colors } from '../constants/colors';
import { X, Loader2, Package } from 'lucide-react';
import { Img } from './Img';
import {
  createShipment,
  updateShipmentTracking,
} from '../api/client';
import type { ItemSaleWithPayment } from '../types';

const CARRIERS = [
  { value: 'USPS', label: 'USPS', logo: '/img/logos/carrier-usps.svg' },
  { value: 'UPS', label: 'UPS', logo: '/img/logos/carrier-ups.svg' },
  { value: 'FedEx', label: 'FedEx', logo: '/img/logos/carrier-fx.svg' },
  { value: 'Other', label: 'Other', logo: null },
];

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

interface ShipTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  orderId: string;
  unshippedItems: ItemSaleWithPayment[];
}

export function ShipTrackingModal({
  isOpen,
  onClose,
  onComplete,
  orderId,
  unshippedItems,
}: ShipTrackingModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(unshippedItems.map(i => i.id))
  );
  const [carrier, setCarrier] = useState('');
  const [customCarrier, setCustomCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const resolvedCarrier = carrier === 'Other' ? customCarrier.trim() : carrier;

  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === unshippedItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(unshippedItems.map(i => i.id)));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvedCarrier || !trackingNumber.trim() || selectedIds.size === 0) {
      setError('Please select at least one item and fill in all fields');
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      const shipment = await createShipment(orderId, [...selectedIds]);
      await updateShipmentTracking(shipment.id, {
        carrier: resolvedCarrier,
        trackingNumber: trackingNumber.trim(),
      });
      onComplete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tracking');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Add Tracking</h2>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.twoColumnBody}>
          {/* Left column — item selection */}
          <div style={styles.leftColumn}>
            <div style={styles.leftHeader}>
              <span style={styles.leftTitle}>Items to Ship</span>
              <button style={styles.toggleLink} onClick={toggleAll}>
                {selectedIds.size === unshippedItems.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div style={styles.itemList}>
              {unshippedItems.map(item => (
                <label key={item.id} style={styles.itemRow}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                    style={styles.checkbox}
                  />
                  {item.itemSnapshot?.imageUrl ? (
                    <Img src={item.itemSnapshot.imageUrl} alt={item.itemSnapshot.name} variant="thumb" renderedWidth={40} blurhash={item.itemSnapshot.blurhash} style={styles.itemThumb} />
                  ) : (
                    <div style={styles.itemThumbPlaceholder}>
                      <Package size={16} color={colors.gray[300]} />
                    </div>
                  )}
                  <div style={styles.itemDetails}>
                    <span style={styles.itemName}>{item.itemSnapshot?.name ?? 'Unknown Item'}</span>
                    <span style={styles.itemPrice}>{formatCurrency(item.winningBid)}</span>
                  </div>
                </label>
              ))}
            </div>
            <div style={styles.leftFooter}>
              {selectedIds.size} of {unshippedItems.length} items selected
            </div>
          </div>

          {/* Right column — carrier & tracking */}
          <div style={styles.rightColumn}>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Carrier</label>
                <div style={styles.carrierGrid}>
                  {CARRIERS.map(c => {
                    const isSelected = carrier === c.value;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        style={{
                          ...styles.carrierCard,
                          ...(isSelected ? styles.carrierCardSelected : {}),
                        }}
                        onClick={() => setCarrier(c.value)}
                      >
                        {c.logo ? (
                          <img src={c.logo} alt={c.label} style={styles.carrierLogo} />
                        ) : (
                          <Package size={24} color={isSelected ? colors.brand.blue : colors.gray[400]} />
                        )}
                        <span style={{
                          ...styles.carrierName,
                          ...(isSelected ? { color: colors.brand.blue } : {}),
                        }}>
                          {c.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {carrier === 'Other' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Carrier Name</label>
                  <input
                    type="text"
                    value={customCarrier}
                    onChange={e => setCustomCarrier(e.target.value)}
                    placeholder="Enter carrier name"
                    style={styles.input}
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Tracking Number</label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={e => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  style={styles.input}
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.cancelButton}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    ...((isSubmitting || !resolvedCarrier || !trackingNumber.trim() || selectedIds.size === 0) ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  disabled={isSubmitting || !resolvedCarrier || !trackingNumber.trim() || selectedIds.size === 0}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    'Save Tracking'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '780px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: colors.ink.black,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  twoColumnBody: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
  },
  leftColumn: {
    borderRight: `1px solid ${colors.gray[300]}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  leftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px 8px',
  },
  leftTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  toggleLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: colors.brand.blue,
    fontWeight: 500,
    padding: 0,
  },
  itemList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 20px',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 0',
    borderBottom: `1px solid ${colors.gray[200]}`,
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  itemThumb: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  itemThumbPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflow: 'hidden',
    flex: 1,
  },
  itemName: {
    fontSize: '13px',
    fontWeight: 500,
    color: colors.ink.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemPrice: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  leftFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.gray[300]}`,
    fontSize: '12px',
    color: colors.gray[600],
    fontWeight: 500,
    flexShrink: 0,
  },
  rightColumn: {
    padding: '20px 24px',
    overflowY: 'auto',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[700],
    marginBottom: '8px',
  },
  carrierGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
  },
  carrierCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '12px 8px',
    backgroundColor: colors.gray[100],
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '10px',
    cursor: 'pointer',
  },
  carrierCardSelected: {
    borderColor: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
  },
  carrierLogo: {
    height: '24px',
    width: 'auto',
    objectFit: 'contain',
  },
  carrierName: {
    fontSize: '11px',
    fontWeight: 600,
    color: colors.gray[600],
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  error: {
    color: '#DC2626',
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
